// ============================================================================
// MeasurementForm — capture a FULL measurement entry in a Drawer:
//   • date taken (DateField, defaults to today)
//   • weight + manual body-fat %
//   • every circumference: neck, shoulders, chest, waist, abdomen, hip,
//     arms (L/R), forearms (L/R), thighs (L/R), calves (L/R)
//   • resting HR + blood pressure (systolic / diastolic)
//   • notes
// Optional-first: blank fields are dropped from the payload (only taken_at is
// sent always). Submits via useAddMeasurement, which invalidates the
// measurement + metrics queries so the KPI tiles and trends recompute live.
// ============================================================================
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Banner, Button, DateField, TextField } from "../../components";
import { useAddMeasurement } from "../../hooks/measurements";
import type { MeasurementInput } from "../../api";
import { Drawer } from "./Drawer";
import { todayIso } from "./lib";

interface MeasurementFormProps {
  clientId: number;
  open: boolean;
  onClose: () => void;
}

// Numeric circumference / vitals fields → [key, i18n label key, unit].
const CM_FIELDS: Array<[keyof MeasurementInput, string]> = [
  ["neck_cm", "neck"],
  ["shoulders_cm", "shoulders"],
  ["chest_cm", "chest"],
  ["waist_cm", "waist"],
  ["abdomen_cm", "abdomen"],
  ["hip_cm", "hip"],
  ["arm_l_cm", "armL"],
  ["arm_r_cm", "armR"],
  ["forearm_l_cm", "forearmL"],
  ["forearm_r_cm", "forearmR"],
  ["thigh_l_cm", "thighL"],
  ["thigh_r_cm", "thighR"],
  ["calf_l_cm", "calfL"],
  ["calf_r_cm", "calfR"],
];

type FieldMap = Record<string, string>;

function emptyFields(): FieldMap {
  const m: FieldMap = { weight_kg: "", body_fat_pct_manual: "", resting_hr: "", bp_systolic: "", bp_diastolic: "", notes: "" };
  for (const [k] of CM_FIELDS) m[k as string] = "";
  return m;
}

export function MeasurementForm({ clientId, open, onClose }: MeasurementFormProps) {
  const { t } = useTranslation("clientDetail");
  const add = useAddMeasurement();

  const [takenAt, setTakenAt] = useState(todayIso());
  const [fields, setFields] = useState<FieldMap>(emptyFields());

  useEffect(() => {
    if (!open) return;
    setTakenAt(todayIso());
    setFields(emptyFields());
  }, [open]);

  const set = (key: string, value: string) =>
    setFields((prev) => ({ ...prev, [key]: value }));

  const numOrUndef = (s: string): number | undefined => {
    const v = s.trim();
    if (v === "") return undefined;
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  };

  const submit = () => {
    const body: MeasurementInput = { taken_at: takenAt };
    const assignNum = (key: keyof MeasurementInput) => {
      const n = numOrUndef(fields[key as string] ?? "");
      if (n !== undefined) (body as Record<string, unknown>)[key as string] = n;
    };
    assignNum("weight_kg");
    assignNum("body_fat_pct_manual");
    assignNum("resting_hr");
    assignNum("bp_systolic");
    assignNum("bp_diastolic");
    for (const [k] of CM_FIELDS) assignNum(k);
    const notes = fields.notes.trim();
    if (notes) body.notes = notes;

    add.mutate(
      { clientId, body },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t("measure.title")}
      subtitle={t("measure.subtitle")}
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={add.isPending}>
            {t("actions.cancel")}
          </Button>
          <Button onClick={submit} disabled={add.isPending}>
            {add.isPending ? t("actions.saving") : t("measure.save")}
          </Button>
        </div>
      }
    >
      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DateField
          label={t("measure.takenAt")}
          value={takenAt}
          onChange={setTakenAt}
        />

        <Section title={t("measure.coreTitle")}>
          <div className="grid grid-cols-2 gap-4">
            <TextField
              label={t("measure.weight")}
              value={fields.weight_kg}
              onChange={(e) => set("weight_kg", e.target.value)}
              type="number"
              inputMode="decimal"
              step="0.1"
              min={0}
              suffix="kg"
            />
            <TextField
              label={t("measure.bodyFat")}
              value={fields.body_fat_pct_manual}
              onChange={(e) => set("body_fat_pct_manual", e.target.value)}
              type="number"
              inputMode="decimal"
              step="0.1"
              min={0}
              max={100}
              suffix="%"
              hint={t("measure.bodyFatHint")}
            />
          </div>
        </Section>

        <Section title={t("measure.circumferenceTitle")}>
          <div className="grid grid-cols-2 gap-4">
            {CM_FIELDS.map(([key, labelKey]) => (
              <TextField
                key={key as string}
                label={t(`measure.cm.${labelKey}`)}
                value={fields[key as string]}
                onChange={(e) => set(key as string, e.target.value)}
                type="number"
                inputMode="decimal"
                step="0.1"
                min={0}
                suffix="cm"
              />
            ))}
          </div>
        </Section>

        <Section title={t("measure.vitalsTitle")}>
          <div className="grid grid-cols-3 gap-4">
            <TextField
              label={t("measure.restingHr")}
              value={fields.resting_hr}
              onChange={(e) => set("resting_hr", e.target.value)}
              type="number"
              inputMode="numeric"
              min={0}
              suffix="bpm"
            />
            <TextField
              label={t("measure.bpSystolic")}
              value={fields.bp_systolic}
              onChange={(e) => set("bp_systolic", e.target.value)}
              type="number"
              inputMode="numeric"
              min={0}
            />
            <TextField
              label={t("measure.bpDiastolic")}
              value={fields.bp_diastolic}
              onChange={(e) => set("bp_diastolic", e.target.value)}
              type="number"
              inputMode="numeric"
              min={0}
            />
          </div>
        </Section>

        <div className="w-full">
          <label
            htmlFor="measure-notes"
            className="mb-1.5 block font-display text-sm font-bold text-body"
          >
            {t("measure.notes")}
          </label>
          <textarea
            id="measure-notes"
            value={fields.notes}
            onChange={(e) => set("notes", e.target.value)}
            rows={2}
            placeholder={t("measure.notesPlaceholder")}
            className="w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
          />
        </div>

        {add.isError && (
          <Banner tone="danger" title={t("measure.saveError")}>
            {t("measure.saveErrorBlurb")}
          </Banner>
        )}
      </form>
    </Drawer>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset>
      <legend className="mb-3 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
        {title}
      </legend>
      {children}
    </fieldset>
  );
}
